import React from 'react'
import cn, { Argument as ClassName } from 'classnames'
import styled from 'styled-components'
import ReactPaginate from 'react-paginate'
import ReactSelect, { components } from 'react-select'
import { useMediaPredicate } from 'react-media-hook'

import Text from '../../ui/Text'
import { theme } from '../theme'
import { ArrowIcon } from '../../common/icons/arrow-icon'
import useText from '../../common/hooks/useText'

const SDiv = styled.div`
    margin-top: ${({ theme }) => theme.sizing.margin.l};
`

const SWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;

    @media (max-width: 900px) {
        padding-bottom: 15px;
    }
`

const SShowedResults = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 14px;
    font-weight: 400;
    padding-bottom: 9px;
`

const SPaginationElement = styled.a<{ selected?: boolean }>`
    color: ${({ selected, theme }) => (selected ? theme.font.darkestColor : theme.brandColors.CA_Blue)};
    text-decoration: none;
    cursor: ${({ selected }) => (selected ? 'not-allowed' : 'pointer')};
    font-size: 14px;
    font-weight: 500;
    font-family: ${({ theme }) => theme.font.extendedName};

    &:hover {
        opacity: ${({ selected }) => (selected ? 'none' : 0.5)};
        color: ${({ selected, theme }) => (selected ? theme.font.darkestColor : theme.brandColors.CA_Blue)};
        text-decoration: none;
    }
`

const SDesktopPaginationWrapper = styled.div<{ showButtons: boolean }>`
    *:focus {
        outline: none;
    }

    *:active {
        box-shadow: none;
    }

    .btn {
        font-size: 14px;
        font-weight: 500;
        padding: ${({ showButtons }) => (showButtons ? '0px 8px' : '0 19px')};

        &:hover {
            background: none;
        }

        @media (max-width: 900px) {
            padding: 0 10px;
        }
    }

    .btn a {
        color: ${({ theme }) => theme.brandColors.CA_Blue};

        &:hover {
            text-decoration: none;
            opacity: 0.5;
        }
    }

    .break a:hover,
    .active a:hover,
    .disabled a:hover {
        opacity: 1;
    }

    .page {
        cursor: auto;
    }

    .buttons {
        margin: 0;
        padding: 0 19px;

        @media (max-width: 900px) {
            padding: 0 5px;
        }
    }

    .active {
        box-shadow: none;

        a {
            color: ${({ theme }) => theme.font.darkestColor};
            text-decoration: underline;

            &:hover {
                cursor: not-allowed;
                text-decoration: underline;
            }
        }
    }

    .disabled {
        opacity: 1;

        a {
            color: ${({ theme }) => theme.font.darkestColor};
            cursor: not-allowed;

            &:hover {
                text-decoration: none;
            }
        }
    }

    .break a {
        color: ${({ theme }) => theme.font.darkestColor};
    }
`

const SMobilePaginationElement = styled(SPaginationElement)`
    padding: 0 15px;
`

const SMobilePaginationWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`

const DropdownIndicator = (props: IndicatorProps<OptionTypeBase, false>) => (
    <components.DropdownIndicator {...props}>
        <ArrowIcon width={15} color={theme.font.darkestColor} />
    </components.DropdownIndicator>
)

export type ChargersPaginationProps = {
    setPageIndex: (index: number) => void
    totalCount: number
    pageSize: number
    pageIndex: number
    displayedChargers: number
    className?: ClassName
    style?: React.CSSProperties
    showButtons?: boolean
    showSummary?: boolean
}

export function ChargersPagination({
    className,
    style,
    totalCount,
    pageIndex,
    displayedChargers,
    pageSize,
    setPageIndex,
    showButtons = true,
    showSummary = true,
}: ChargersPaginationProps): React.ReactElement {
    const text = useText()
    const pageCount = Math.ceil(totalCount / pageSize)

    const getMobileOptions = () => {
        const mobileOptions = []
        for (let i = 0; i < pageCount; i++) {
            mobileOptions.push({ label: i + 1, value: i })
        }
        return mobileOptions
    }
    const isMobileScreen = useMediaPredicate('(max-width: 600px)')
    const firstChargerIndex = displayedChargers > 0 ? displayedChargers * pageIndex + 1 : 0
    const lastIndex = displayedChargers * pageIndex + displayedChargers
    const lastChargerIndex = lastIndex < totalCount ? lastIndex : totalCount

    return (
        <SDiv className={cn(className)} style={style} data-testid="pagination">
            {showSummary && (
                <SShowedResults data-testid="showed-results">
                    <Text
                        code="lblShowedResults"
                        values={{
                            firstChargerIndex,
                            lastChargerIndex,
                            totalChargers: totalCount,
                        }}
                    />
                </SShowedResults>
            )}
            {totalCount > pageSize && (
                <SWrapper>
                    {showButtons && (
                        <SPaginationElement
                            data-testid="button-first"
                            selected={pageIndex === 0}
                            onClick={() => {
                                setPageIndex(0)
                            }}
                        >
                            <Text code="btnFirst" />
                        </SPaginationElement>
                    )}
                    {isMobileScreen ? (
                        <SMobilePaginationWrapper>
                            <SMobilePaginationElement
                                selected={pageSize === 0}
                                onClick={() => setPageIndex(pageIndex - 1)}
                            >
                                <Text code="btnPreviousShort" />
                            </SMobilePaginationElement>
                            <ReactSelect
                                components={{ DropdownIndicator }}
                                options={getMobileOptions()}
                                menuPlacement="top"
                                value={{ label: pageIndex + 1, value: pageIndex }}
                                onChange={(option) => setPageIndex(option?.value || 0)}
                                styles={{
                                    indicatorSeparator: () => ({ display: 'none' }),
                                    container: (styles) => ({ ...styles, width: '80px' }),
                                    valueContainer: (styles) => ({
                                        ...styles,
                                        justifyContent: 'flex-end',
                                        padding: 0,
                                    }),
                                    control: (styles) => ({
                                        ...styles,
                                        minHeight: '35px',
                                        borderColor: theme.font.midLightColor,
                                    }),
                                    dropdownIndicator: (styles) => ({
                                        ...styles,
                                        padding: '6px 8px',
                                        transform: 'rotate(180deg)',
                                    }),
                                    singleValue: (styles) => ({
                                        ...styles,
                                        marginRight: 4,
                                        fontSize: 16,
                                    }),
                                }}
                                isSearchable={false}
                            />
                            <SMobilePaginationElement
                                selected={pageIndex === pageSize - 1}
                                onClick={() => setPageIndex(pageIndex + 1)}
                            >
                                <Text code="btnNext" />
                            </SMobilePaginationElement>
                        </SMobilePaginationWrapper>
                    ) : (
                        <SDesktopPaginationWrapper showButtons={!showButtons}>
                            <ReactPaginate
                                previousLabel={isMobileScreen ? '<' : text('btnPreviousShort')}
                                nextLabel={isMobileScreen ? '>' : text('btnNext')}
                                breakLabel="..."
                                marginPagesDisplayed={2}
                                forcePage={pageIndex}
                                pageCount={pageCount}
                                onPageChange={(data) => {
                                    setPageIndex(data.selected)
                                }}
                                pageRangeDisplayed={2}
                                breakClassName="btn break"
                                previousClassName="btn prev"
                                pageClassName="btn page"
                                nextClassName="btn next"
                                containerClassName="buttons"
                                activeClassName="active"
                                disabledClassName="disabled"
                            />
                        </SDesktopPaginationWrapper>
                    )}

                    {showButtons && (
                        <SPaginationElement
                            data-testid="button-last"
                            selected={pageIndex === pageCount - 1}
                            onClick={() => setPageIndex(pageCount - 1)}
                        >
                            <Text code="btnLast" />
                        </SPaginationElement>
                    )}
                </SWrapper>
            )}
        </SDiv>
    )
}
